import { useEffect, useState, useCallback } from "react";
import { api, fmtKg, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Scales } from "@phosphor-icons/react";
import { PageLoading } from "@/components/layout/Layout";
import { useT } from "@/contexts/I18nContext";
import { ListState } from "@/components/ListState";

export default function Weighings() {
    const { t } = useT();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ rfid: "", weight_kg: "", timestamp: "", production_system: "", diet_type: "" });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/weighings", { params: { limit: 200 } });
            setItems(r.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/weighings", {
                rfid: form.rfid,
                weight_kg: Number(form.weight_kg),
                timestamp: form.timestamp ? new Date(form.timestamp).toISOString() : null,
                production_system: form.production_system || null,
                diet_type: form.diet_type || null,
            });
            toast.success(t("weighing_registered"));
            setOpen(false);
            setForm({ rfid: "", weight_kg: "", timestamp: "", production_system: "", diet_type: "" });
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="weighings-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{t("weighings_title")}</h1>
                    <p className="text-sm text-muted-foreground mt-1">{t("weighings_subtitle")}</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-[#083D1E] hover:bg-[#052A14] text-white" data-testid="new-weighing-btn">
                            <Plus size={16} className="mr-2" /> {t("new_weighing")}
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-display">{t("new_weighing_title")}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-4">
                            <div className="space-y-1.5">
                                <Label>{t("animal_rfid")}</Label>
                                <Input required value={form.rfid} onChange={(e) => setForm({ ...form, rfid: e.target.value })} data-testid="weighing-rfid-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t("weight_kg_label")}</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    required
                                    value={form.weight_kg}
                                    onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                                    data-testid="weighing-kg-input"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t("datetime_optional")}</Label>
                                <Input
                                    type="datetime-local"
                                    value={form.timestamp}
                                    onChange={(e) => setForm({ ...form, timestamp: e.target.value })}
                                    data-testid="weighing-ts-input"
                                />
                            </div>
                            <div className="border-t pt-4 mt-2 space-y-3">
                                <div className="text-xs text-muted-foreground">{t("period_override")}</div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">{t("production_system")}</Label>
                                    <Select value={form.production_system} onValueChange={(v) => setForm({ ...form, production_system: v })}>
                                        <SelectTrigger data-testid="weighing-system-select">
                                            <SelectValue placeholder="—" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="feedlot">{t("sys_feedlot")}</SelectItem>
                                            <SelectItem value="semi">{t("sys_semi")}</SelectItem>
                                            <SelectItem value="pasture">{t("sys_pasture")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">{t("diet_type_optional")}</Label>
                                    <Input
                                        value={form.diet_type}
                                        onChange={(e) => setForm({ ...form, diet_type: e.target.value })}
                                        placeholder={t("diet_placeholder")}
                                        data-testid="weighing-diet-input"
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="submit" disabled={saving} className="bg-[#083D1E] hover:bg-[#052A14] text-white" data-testid="weighing-save-btn">
                                    {saving ? t("saving") : t("register_weighing")}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <Card className="card-paper shadow-none overflow-hidden">
                <ListState
                    loading={loading}
                    isEmpty={items.length === 0}
                    emptyIcon={Scales}
                    emptyTitle={t("no_weighings")}
                    emptyHint={t("register_first_weighing")}
                >
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t("date")}</TableHead>
                                <TableHead>{t("rfid")}</TableHead>
                                <TableHead className="text-right">{t("weight")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((w) => (
                                <TableRow key={w.id} data-testid={`weighing-row-${w.id}`}>
                                    <TableCell>{fmtDate(w.timestamp)}</TableCell>
                                    <TableCell className="font-medium">{w.rfid}</TableCell>
                                    <TableCell className="text-right metric-number">{fmtKg(w.weight_kg)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </ListState>
            </Card>
        </div>
    );
}
